import { createAsyncThunk } from "@reduxjs/toolkit";
import fetchAPI from "../../../utils/axios";

const getBookByIdQuery = (bookId) => `
query {
    bookByBookID(input: {bookId: "${bookId}", isOwnBookRequested:true}) {
        id
        title
        author
        level
        imageUrl
        paragraphs {
            id
            paragraph
        }
        categories
        ratings
        availability
    }
}
`;

/**
 * Thunk to add a new own book for premium users
 * 
 * @async
 * @function addOwnBook
 * @param {Object} bookObj - Book object containing title, image, author, level, paragraphs
 * @param {function} thunkAPI - Redux thunk API
 * @returns {Promise<Object>} Response with success message
 */
export const addOwnBook = createAsyncThunk(
    'premium/addOwnBook',
    async (bookObj, thunkAPI) => {
        try {
            const response = await fetchAPI.post('/ownbooks', {
                book: bookObj
            });

            if (response.data.success) {
                return response.data;
            }
            return thunkAPI.rejectWithValue(response.data.message || 'Failed to add book');
        } catch (error) {
            let message = error.response?.data?.error || error.message || 'Failed to add own book';
            return thunkAPI.rejectWithValue(message);
        }
    }
);

/**
 * Thunk to fetch all own books for premium users
 * 
 * @async
 * @function getAllOwnBooks
 * @returns {Promise<Object>} Response containing array of own books
 */
export const getAllOwnBooks = createAsyncThunk(
    'premium/getAllOwnBooks',
    async (_, thunkAPI) => {
        try {
            const response = await fetchAPI.get('/ownbooks');

            if (response.data.success) {
                return response.data.data;
            }
            return thunkAPI.rejectWithValue(response.data.message || 'Failed to fetch own books');
        } catch (error) {
            let message = error.response?.data?.error || error.message || 'Failed to fetch own books';
            return thunkAPI.rejectWithValue(message);
        }
    }
);

/**
 * Thunk to delete own books for premium users
 * 
 * @async
 * @function deleteOwnBooks
 * @param {Array<string>} bookIds - Array of book IDs to delete
 * @param {function} thunkAPI - Redux thunk API
 * @returns {Promise<Object>} Response with updated books list and deletion message
 */
export const deleteOwnBooks = createAsyncThunk(
    'premium/deleteOwnBooks',
    async (bookIds, thunkAPI) => {
        try {
            const response = await fetchAPI.delete('/ownbooks', {
                data: {
                    bookIds
                }
            });

            if (response.data.success) {
                return response.data.data;
            }
            return thunkAPI.rejectWithValue(response.data.message || 'Failed to delete books');
        } catch (error) {
            let message = error.response?.data?.error || error.message || 'Failed to delete own books';
            return thunkAPI.rejectWithValue(message);
        }
    }
);

/**
 * Thunk to fetch own book details by book id using GraphQL
 *
 * @async
 * @function fetchOwnBookById
 * @param {string} bookId - Own book ID
 * @param {function} thunkAPI - Redux thunk API
 * @returns {Promise<Object>} Detailed own book object
 */
export const fetchOwnBookById = createAsyncThunk(
    'premium/fetchOwnBookById',
    async (bookId, thunkAPI) => {
        try {
            const defaultBaseUrl = fetchAPI.defaults.baseURL.replace('/api/v1', '');
            const response = await fetchAPI.post(
                '/graphql',
                {
                    query: getBookByIdQuery(bookId)
                },
                {
                    baseURL: defaultBaseUrl
                }
            );

            if (response.data.errors) {
                const errorMessage = response.data.errors[0]?.message || 'GraphQL Error';
                return thunkAPI.rejectWithValue(errorMessage);
            }

            const book = response.data?.data?.bookByBookID;
            if (!book) {
                return thunkAPI.rejectWithValue('Book details were not found');
            }

            return book;
        } catch (error) {
            let message = error.response?.data?.error || error.message || 'Failed to fetch book details';
            return thunkAPI.rejectWithValue(message);
        }
    }
);
